import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Phone, MapPin, Globe, Pencil, Plus, Store, Wallet,
  Megaphone, Image as ImageIcon, Activity, ExternalLink, CheckCircle2,
  XCircle, BarChart3,
} from "lucide-react";

const TABS = [
  { key: "overview",  label: "Overview",  icon: BarChart3 },
  { key: "portfolio", label: "Portfolio", icon: ImageIcon },
  { key: "ads",       label: "Ads",       icon: Megaphone },
  { key: "payments",  label: "Payments",  icon: Wallet },
  { key: "activity",  label: "Activity",  icon: Activity },
];

const fmtMoney = (n, c = "INR") => {
  try { return Number(n || 0).toLocaleString("en-IN", { style: "currency", currency: c, maximumFractionDigits: 0 }); }
  catch { return `₹${n}`; }
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function RestaurantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/restaurants/${id}/overview`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load restaurant");
      if (e.response?.status === 404) navigate("/admin/restaurants");
    } finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="p-6 lg:p-10 text-sm text-[#525252]">Loading restaurant…</div>;
  }

  const { restaurant: r, totals, portfolios, ads, payments, activity } = data;

  return (
    <div className="p-6 lg:p-10 space-y-8 w-full" data-testid="restaurant-detail-page">
      <div>
        <button onClick={() => navigate("/admin/restaurants")} data-testid="restaurant-detail-back"
                className="inline-flex items-center gap-1 text-sm text-[#525252] hover:text-[var(--brand-primary)] mb-4">
          <ArrowLeft size={14}/> All restaurants
        </button>

        <HeaderCard restaurant={r} totals={totals}/>
      </div>

      {/* Tabs */}
      <div className="border-b border-black/10 flex flex-wrap gap-1" data-testid="restaurant-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`restaurant-tab-${t.key}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      active
                        ? "border-[var(--brand-primary)] text-[var(--brand-primary)]"
                        : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
                    }`}>
              <Icon size={14}/> {t.label}
              <CountPill tab={t.key} totals={totals}/>
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === "overview"  && <OverviewTab totals={totals} ads={ads} payments={payments} portfolios={portfolios}/>}
      {tab === "portfolio" && <PortfolioTab items={portfolios} restaurantId={r.id}/>}
      {tab === "ads"       && <AdsTab items={ads} restaurantId={r.id}/>}
      {tab === "payments"  && <PaymentsTab items={payments} restaurantId={r.id}/>}
      {tab === "activity"  && <ActivityTab items={activity}/>}
    </div>
  );
}

function HeaderCard({ restaurant: r, totals }) {
  const paidLabel = totals.is_paid_now
    ? `Paid until ${fmtDate(totals.paid_until)}`
    : (totals.paid_until ? `Expired · last ended ${fmtDate(totals.paid_until)}` : "Unpaid");

  return (
    <div className="border border-black/10 rounded-sm bg-white p-6 lg:p-8 flex flex-col lg:flex-row gap-6">
      {/* Logo */}
      <div className="shrink-0">
        {r.logo_image_id ? (
          <img src={fileUrl(r.logo_image_id)} alt={r.name}
               className="w-24 h-24 rounded-sm object-cover bg-[#FAFAFA] border border-black/5"/>
        ) : (
          <div className="w-24 h-24 rounded-sm bg-[#FAFAFA] flex items-center justify-center border border-black/5">
            <Store size={28} className="text-[#9CA3AF]"/>
          </div>
        )}
      </div>

      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-3 flex-wrap">
          <h1 data-testid="restaurant-detail-name"
              className="font-display text-3xl font-black tracking-tight">{r.name}</h1>
          <span data-testid="restaurant-detail-active-pill"
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${
                  r.is_active ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-zinc-200 bg-zinc-100 text-zinc-600"}`}>
            {r.is_active ? <CheckCircle2 size={10}/> : <XCircle size={10}/>}
            {r.is_active ? "Active" : "Inactive"}
          </span>
          <span data-testid="restaurant-detail-paid-pill"
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${
                  totals.is_paid_now ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                     : "border-rose-200 bg-rose-50 text-rose-700"}`}>
            <Wallet size={10}/> {paidLabel}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#525252]">
          {r.address && <Line icon={MapPin}>{r.address}</Line>}
          {r.phone && <Line icon={Phone}><a href={`tel:${r.phone}`} className="hover:text-[var(--brand-primary)]">{r.phone}</a></Line>}
          {r.email && <Line icon={Mail}><a href={`mailto:${r.email}`} className="hover:text-[var(--brand-primary)]">{r.email}</a></Line>}
          {r.zomato_url && <Line icon={Globe}><ExtLink href={r.zomato_url}>Zomato</ExtLink></Line>}
          {r.swiggy_url && <Line icon={Globe}><ExtLink href={r.swiggy_url}>Swiggy</ExtLink></Line>}
        </div>

        {Array.isArray(r.services) && r.services.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {r.services.map((s) => (
              <span key={s} className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#FAFAFA] border border-black/5 rounded-sm">{s}</span>
            ))}
          </div>
        )}

        {/* KPI strip */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Kpi label="Ads"        value={totals.campaigns}                  sub={`${totals.active_campaigns} live`}/>
          <Kpi label="Portfolios" value={totals.portfolios}                 sub={`${totals.approved_portfolios} approved`}/>
          <Kpi label="Revenue"    value={fmtMoney(totals.total_revenue)}    sub={`${totals.payments_count} invoices`}/>
          <Kpi label="Ad CTR"     value={`${totals.ctr_percent}%`}          sub={`${totals.ad_views_total} views · ${totals.ad_clicks_total} clicks`}/>
        </div>
      </div>

      {/* Edit button */}
      <div className="shrink-0">
        <Link to={`/admin/restaurants?edit=${r.id}`} data-testid="restaurant-detail-edit"
              className="inline-flex items-center gap-2 px-4 py-2 border border-black/10 rounded-sm text-sm hover:bg-black/5">
          <Pencil size={14}/> Edit master
        </Link>
      </div>
    </div>
  );
}

const Line = ({ icon: Icon, children }) => (
  <div className="inline-flex items-center gap-2"><Icon size={12}/> <span className="truncate">{children}</span></div>
);
const ExtLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[var(--brand-primary)]">
    {children} <ExternalLink size={10}/>
  </a>
);
const Kpi = ({ label, value, sub }) => (
  <div className="border border-black/10 rounded-sm bg-[#FAFAFA] px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-[#525252]">{label}</div>
    <div className="font-display font-bold text-lg leading-tight">{value}</div>
    {sub && <div className="text-[10px] text-[#9CA3AF] mt-0.5">{sub}</div>}
  </div>
);
const CountPill = ({ tab, totals }) => {
  const n = ({
    portfolio: totals.portfolios,
    ads: totals.campaigns,
    payments: totals.payments_count,
  })[tab];
  if (!n) return null;
  return <span className="text-[10px] bg-black/5 text-[#525252] px-1.5 py-0.5 rounded-sm tabular-nums">{n}</span>;
};

// ---------- Tabs ----------

function OverviewTab({ totals, ads, payments }) {
  const latestAd = ads[0];
  const latestPayment = payments[0];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="restaurant-overview-tab">
      <Panel title="Latest advertisement">
        {latestAd ? (
          <div className="space-y-1 text-sm">
            <div className="font-medium">{latestAd.placements?.map((p) => `${p.page}/${p.slot}`).join(", ") || "—"}</div>
            <div className="text-xs text-[#525252]">Status: {latestAd.status} · {latestAd.is_active ? "Active" : "Paused"}</div>
            <div className="text-xs text-[#525252]">Views: {latestAd.view_count || 0} · Clicks: {latestAd.click_count || 0}</div>
            <div className="text-xs text-[#525252]">Created {fmtDate(latestAd.created_at)}</div>
          </div>
        ) : <Empty text="No advertisements yet"/>}
      </Panel>
      <Panel title="Latest payment">
        {latestPayment ? (
          <div className="space-y-1 text-sm">
            <div className="font-mono text-xs">{latestPayment.invoice_number}</div>
            <div className="font-semibold">{fmtMoney(latestPayment.amount, latestPayment.currency)}</div>
            <div className="text-xs text-[#525252]">{latestPayment.plan} · {fmtDate(latestPayment.period_start)} → {fmtDate(latestPayment.period_end)}</div>
            <div className="text-xs text-[#525252]">Paid {fmtDate(latestPayment.payment_date)} via {latestPayment.method}</div>
          </div>
        ) : <Empty text="No payments recorded"/>}
      </Panel>
      <Panel title="Performance">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div><div className="font-display font-bold text-xl">{totals.ad_views_total}</div><div className="text-[10px] uppercase text-[#525252] mt-0.5">Views</div></div>
          <div><div className="font-display font-bold text-xl">{totals.ad_clicks_total}</div><div className="text-[10px] uppercase text-[#525252] mt-0.5">Clicks</div></div>
          <div><div className="font-display font-bold text-xl">{totals.ctr_percent}%</div><div className="text-[10px] uppercase text-[#525252] mt-0.5">CTR</div></div>
        </div>
      </Panel>
      <Panel title="Billing snapshot">
        <div className="space-y-1 text-sm">
          <div className="font-display font-bold text-2xl">{fmtMoney(totals.total_revenue)}</div>
          <div className="text-xs text-[#525252]">Lifetime revenue across {totals.payments_count} invoice(s)</div>
          <div className={`text-xs mt-2 ${totals.is_paid_now ? "text-emerald-700" : "text-rose-700"}`}>
            {totals.is_paid_now ? `Currently paid until ${fmtDate(totals.paid_until)}` : "No active paid period — ads are paused"}
          </div>
        </div>
      </Panel>
    </div>
  );
}

function PortfolioTab({ items, restaurantId }) {
  return (
    <div data-testid="restaurant-portfolio-tab">
      <ListHeader title="Portfolio items" count={items.length}
                  ctaLabel="Add portfolio"
                  ctaTo={`/admin/portfolio?restaurant=${restaurantId}&new=1`}/>
      {items.length === 0 ? <Empty text="No portfolio items yet"/> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <Link to={`/portfolio/${p.slug || p.id}`} target="_blank" rel="noreferrer"
                  key={p.id} className="border border-black/10 rounded-sm bg-white overflow-hidden hover:border-[var(--brand-primary)] transition-colors">
              {(p.after_image_id || p.before_image_id) ? (
                <img src={fileUrl(p.after_image_id || p.before_image_id)} alt="" className="w-full aspect-[4/3] object-cover"/>
              ) : <div className="w-full aspect-[4/3] bg-[#FAFAFA]"/>}
              <div className="p-3">
                <div className="font-medium text-sm truncate">{p.title}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] uppercase tracking-wider text-[#525252]">{p.category}</span>
                  <StatusPill status={p.status}/>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function AdsTab({ items, restaurantId }) {
  return (
    <div data-testid="restaurant-ads-tab">
      <ListHeader title="Advertisements" count={items.length}
                  ctaLabel="Create ad"
                  ctaTo={`/admin/ads?restaurant=${restaurantId}&new=1`}/>
      {items.length === 0 ? <Empty text="No advertisements yet"/> : (
        <div className="border border-black/10 rounded-sm bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] border-b border-black/10">
              <tr>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Placements</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Status</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-[0.15em] text-[#525252]">Views</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-[0.15em] text-[#525252]">Clicks</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Schedule</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Created</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-black/5">
                  <td className="px-4 py-3 text-xs">{(a.placements || []).map((p) => `${p.page}/${p.slot}`).join(", ") || "—"}</td>
                  <td className="px-4 py-3"><StatusPill status={a.status}/> {!a.is_active && <span className="ml-1 text-[10px] text-zinc-500">paused</span>}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{a.view_count || 0}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{a.click_count || 0}</td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{a.starts_at ? fmtDate(a.starts_at) : "—"} → {a.ends_at ? fmtDate(a.ends_at) : "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#525252] whitespace-nowrap">{fmtDate(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PaymentsTab({ items, restaurantId }) {
  return (
    <div data-testid="restaurant-payments-tab">
      <ListHeader title="Payments" count={items.length}
                  ctaLabel="Record payment"
                  ctaTo={`/admin/payments?restaurant=${restaurantId}&new=1`}/>
      {items.length === 0 ? <Empty text="No payments recorded"/> : (
        <div className="border border-black/10 rounded-sm bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] border-b border-black/10">
              <tr>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Invoice</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-[0.15em] text-[#525252]">Amount</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Plan</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Period</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Paid on</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Method</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-black/5">
                  <td className="px-4 py-3 font-mono text-xs">{p.invoice_number}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">{fmtMoney(p.amount, p.currency)}</td>
                  <td className="px-4 py-3 capitalize text-xs">{p.plan}</td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDate(p.period_start)} → {fmtDate(p.period_end)}</td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDate(p.payment_date)}</td>
                  <td className="px-4 py-3 uppercase text-[10px] tracking-wider text-[#525252]">{p.method}</td>
                  <td className="px-4 py-3"><StatusPill status={p.status}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ActivityTab({ items }) {
  return (
    <div data-testid="restaurant-activity-tab">
      <ListHeader title="Recent activity" count={items.length}/>
      {items.length === 0 ? <Empty text="No activity captured yet"/> : (
        <ol className="space-y-2">
          {items.map((a) => (
            <li key={a.id || a.created_at} className="border border-black/10 rounded-sm bg-white px-4 py-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{a.description || a.action}</div>
                  <div className="text-xs text-[#525252] mt-0.5">
                    <span className="font-mono">{a.action}</span> · {a.target_type}
                  </div>
                </div>
                <div className="text-xs text-[#9CA3AF] whitespace-nowrap text-right">
                  <div>{fmtDateTime(a.created_at)}</div>
                  <div>{a.user_email || a.user_id?.slice(0, 8)}</div>
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ---------- Shared bits ----------

function Panel({ title, children }) {
  return (
    <div className="border border-black/10 rounded-sm bg-white p-5">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#525252] mb-3">{title}</div>
      {children}
    </div>
  );
}
const Empty = ({ text }) => <div className="border border-dashed border-black/10 rounded-sm bg-white p-8 text-sm text-[#525252] text-center">{text}</div>;

function ListHeader({ title, count, ctaLabel, ctaTo }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-display text-xl font-bold inline-flex items-center gap-2">
        {title} <span className="text-sm text-[#9CA3AF] font-normal tabular-nums">{count}</span>
      </h2>
      {ctaLabel && ctaTo && (
        <Link to={ctaTo} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-black/10 rounded-sm hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]">
          <Plus size={14}/> {ctaLabel}
        </Link>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const tone = {
    approved: "border-emerald-200 bg-emerald-50 text-emerald-700",
    paid:     "border-emerald-200 bg-emerald-50 text-emerald-700",
    pending:  "border-amber-200 bg-amber-50 text-amber-800",
    refunded: "border-amber-200 bg-amber-50 text-amber-800",
    rejected: "border-rose-200 bg-rose-50 text-rose-700",
    void:     "border-zinc-200 bg-zinc-100 text-zinc-600",
  }[status] || "border-zinc-200 bg-zinc-100 text-zinc-600";
  return <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${tone}`}>{status}</span>;
}
